import { BASE_LANGUAGES, PortalLanguage } from "portal/i18nConstants";
import { Duration } from "luxon";
import { isObject } from "./objects";
import { isUndefined } from "./identity";
import { Message } from "protos/portal/messages";
import { MessageResponse, TranslationLanguages } from "stream-chat";

export const isSameAuthor = (a?: Message, b?: Message): boolean =>
  !isUndefined(a) &&
  !isUndefined(b) &&
  a.authorRobotId === b.authorRobotId &&
  a.authorUserId === b.authorUserId;

export const isSoonAfter = (a?: Message, b?: Message): boolean =>
  !isUndefined(a) &&
  !isUndefined(b) &&
  Math.abs((a.db?.createdAt || Infinity) - (b.db?.createdAt || -Infinity)) <
    Duration.fromObject({ hours: 1 }).toMillis();

// Stream supports all our Portal languages. If we add more Portal languages
// that Stream doesn't support, this'll be a compile-time error.
export const toStreamLanguage = (
  language: PortalLanguage
): TranslationLanguages => {
  return BASE_LANGUAGES[language];
};

export const getMessageTranslation = (
  message: Pick<MessageResponse, "i18n">,
  // ^ works around weird `StreamMessage` typing in `stream-chat-react`
  language: TranslationLanguages
): string | undefined => {
  return message.i18n?.[`${language}_text`];
};

const collatorCache: Map<string, Intl.Collator> = new Map();

const getCollator = (locale: string): Intl.Collator => {
  let collator = collatorCache.get(locale);
  if (!collator) {
    collator = new Intl.Collator(locale, {
      sensitivity: "variant",
      ignorePunctuation: true,
    });
    collatorCache.set(locale, collator);
  }
  return collator;
};

// Tests whether it looks like the given "translated" text is actually the same
// as the original, just maybe modulo tokenization differences.
export const seemsUntranslated = (
  locale: string,
  originalText: string,
  translatedText: string
): boolean => {
  return getCollator(locale).compare(originalText, translatedText) === 0;
};

const ALT_STATUS_OK = "ok";

/** Flat fields on the message root: alt_<lang>_text / alt_<lang>_status. */
export const altTextField = (lang: TranslationLanguages): string =>
  `alt_${lang}_text`;

export const altStatusField = (lang: TranslationLanguages): string =>
  `alt_${lang}_status`;

export const getDeepLAlternate = (
  message: unknown,
  targetLanguage: TranslationLanguages
): string | undefined => {
  if (!isObject(message)) {
    return undefined;
  }
  if (message[altStatusField(targetLanguage)] !== ALT_STATUS_OK) {
    return undefined;
  }
  const text = message[altTextField(targetLanguage)];
  return typeof text === "string" && text.trim() !== "" ? text : undefined;
};

export const channelHasDeepLAlternates = (
  messages: readonly unknown[] | undefined,
  targetLanguage: TranslationLanguages
): boolean => {
  if (!messages) {
    return false;
  }
  return messages.some(
    (m) => getDeepLAlternate(m, targetLanguage) !== undefined
  );
};
